import React, { useState } from "react";
import { cn } from "../../lib/utils";
import { Search, X, ChevronRight } from "lucide-react";
import type { TopBarProps, DashboardShellAction } from "./types";

export function TopBar({
  title,
  contextLabel,
  breadcrumbs,
  leadingSlot,
  searchSlot,
  actions = [],
  profileSlot,
  onAction,
  onBreadcrumbClick,
  className,
}: TopBarProps) {
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const renderAction = (action: DashboardShellAction) => {
    return (
      <button
        key={action.id}
        onClick={() => !action.disabled && onAction?.(action.id)}
        disabled={action.disabled}
        title={action.hint || action.label}
        className={cn(
          "inline-flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium transition-colors",
          "hover:bg-muted hover:text-foreground",
          action.tone === "primary" ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-transparent text-muted-foreground",
          action.tone === "danger" && "text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20",
          action.disabled && "opacity-50 cursor-not-allowed",
          "shrink-0"
        )}
      >
        {action.icon && <span className="w-4 h-4">{action.icon}</span>}
        <span className="sr-only">{action.label}</span>
      </button>
    );
  };

  return (
    <div
      className={cn(
        "relative flex items-center justify-between h-12 sm:h-14 3xl:h-20 4k:h-24 px-3 sm:px-4 3xl:px-8 bg-card",
        className
      )}
    >
      {/* Mobile Search Overlay */}
      {isMobileSearchOpen && searchSlot && (
        <div className="absolute inset-0 z-50 flex items-center px-3 sm:px-4 bg-card animate-in fade-in slide-in-from-top-1">
          <div className="flex-1 w-full max-w-full">
            {searchSlot}
          </div>
          <button
            onClick={() => setIsMobileSearchOpen(false)}
            className="ml-2 inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="w-5 h-5" />
            <span className="sr-only">Close search</span>
          </button>
        </div>
      )}

      {/* Left section: Title, Breadcrumbs & Context */}
      <div className="flex items-center min-w-0">
        {leadingSlot && <div className="mr-3 sm:mr-4 3xl:mr-8 shrink-0">{leadingSlot}</div>}
        <div className="flex flex-col min-w-0 overflow-hidden">
          <div className="flex items-center text-xs sm:text-sm 3xl:text-xl">
            {breadcrumbs && breadcrumbs.length > 0 ? (
              <nav className="flex items-center space-x-1 text-muted-foreground whitespace-nowrap 3xl:space-x-3">
                {breadcrumbs.map((crumb) => (
                  <React.Fragment key={crumb.id}>
                    <button
                      onClick={() => onBreadcrumbClick?.(crumb.id)}
                      className={cn(
                        "hover:text-foreground transition-colors truncate max-w-[100px] sm:max-w-[120px] 3xl:max-w-[240px] font-medium text-[11px] sm:text-xs 3xl:text-lg",
                        crumb.href ? "cursor-pointer" : "cursor-default"
                      )}
                    >
                      {crumb.label}
                    </button>
                    <ChevronRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 3xl:w-5 3xl:h-5 opacity-50 shrink-0" />
                  </React.Fragment>
                ))}
                <h1 className="font-bold text-foreground truncate leading-none text-xs sm:text-sm 3xl:text-2xl ml-0.5 sm:ml-1 3xl:ml-2">
                  {title}
                </h1>
              </nav>
            ) : (
              <h1 className="font-bold truncate leading-none text-foreground text-xs sm:text-sm 3xl:text-3xl">
                {title}
              </h1>
            )}
          </div>
          {contextLabel && (
            <span className="text-[10px] sm:text-xs 3xl:text-lg text-muted-foreground truncate mt-0.5 3xl:mt-1">
              {contextLabel}
            </span>
          )}
        </div>
      </div>

      {/* Middle section: Desktop Search — shown only at lg+ to give md tablets breathing room */}
      {searchSlot && (
        <div className="flex-1 max-w-md mx-6 hidden lg:block">
          {searchSlot}
        </div>
      )}

      {/* Right section: Actions & Profile */}
      <div className="flex items-center gap-2 relative z-10">
        {searchSlot && (
          <button
            onClick={() => setIsMobileSearchOpen(true)}
            className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground shrink-0"
          >
            <Search className="w-4 h-4" />
            <span className="sr-only">Open search</span>
          </button>
        )}
        {actions.length > 0 && (
          <div className="flex items-center gap-1 border-r border-border pr-2 mr-2">
            {actions.map(renderAction)}
          </div>
        )}
        {profileSlot && (
          <div className="flex-shrink-0">
            {profileSlot}
          </div>
        )}
      </div>
    </div>
  );
}